import { createColumnHelper } from "@tanstack/react-table";
import {
  primaryDueBadge,
  type DeliverAxisFilter,
  type DueFilter,
  type PayAxisFilter,
} from "@icraftx/shared/v1";
import { AxisProgress } from "@/modules/core/components/AxisProgress";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import {
  DueBadge,
  FilterPartyName,
} from "@/modules/core/components/DueBadge";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import {
  dueBadgeToFilter,
  toggleFilter,
  togglePartyFilter,
  type PartyFilter,
} from "@/modules/core/list-cell-actions";
import {
  type DeskTableFeatures,
  useDataTableShowRelativeTime,
} from "@/modules/shell/data-table";
import { DeskDocListRowMenu } from "@/modules/shell/DeskDocListRowMenu";
import {
  docDateRelativeLabel,
  formatDocDateLabel,
} from "@/modules/shell/relative-time";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { shouldFlashAfterToggle } from "@/modules/shell/use-list-row-flash";
import { AssigneeAvatars } from "@/modules/tags/components/AssigneeAvatars";
import { ListTagPills } from "@/modules/tags/components/TagPill";
import type { SoListRow } from "@/shared/api/sales";
import type { AssigneeRow } from "@/shared/api/tags";

export type SoTableRow = SoListRow & {
  tags: { id: string; name: string; color?: string | null }[];
  assignees: AssigneeRow[];
};

export type SoListCellActions = {
  partyFilter: PartyFilter | null;
  setPartyFilter: (next: PartyFilter | null) => void;
  payFilter: PayAxisFilter;
  setPayFilter: (next: PayAxisFilter) => void;
  deliverFilter: DeliverAxisFilter;
  setDeliverFilter: (next: DeliverAxisFilter) => void;
  dueFilter: DueFilter;
  setDueFilter: (next: DueFilter) => void;
  tagId: string;
  setTagId: (next: string) => void;
  /** Flash this doc after a filter-from-cell *set* — plan/30. */
  onFilterFromRow?: (docId: string) => void;
  /** Pathname + search for list-return stash — plan/35 D. */
  listPath: string;
};

const columnHelper = createColumnHelper<DeskTableFeatures, SoTableRow>();

function DocDateCell(props: { raw: string }) {
  const showRelative = useDataTableShowRelativeTime();
  const relative = showRelative ? docDateRelativeLabel(props.raw) : "";
  return (
    <span className="flex flex-col tabular-nums">
      <span className="text-sm">{formatDocDateLabel(props.raw)}</span>
      {relative ? (
        <span className="text-xs text-[var(--color-muted)]">{relative}</span>
      ) : null}
    </span>
  );
}

function flashIfSet(
  actions: SoListCellActions,
  docId: string,
  didSet: boolean,
) {
  if (didSet) actions.onFilterFromRow?.(docId);
}

export function salesOrderColumns(
  companySlug: string,
  actions: SoListCellActions,
) {
  return columnHelper.columns([
    columnHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 120,
      cell: (info) => {
        const row = info.row.original;
        const docTo = `/c/${companySlug}/sales/orders/${row.id}`;
        return (
          <DocNumberLink
            to={docTo}
            onClick={() => stashListReturn(actions.listPath, row.id)}
          >
            {info.getValue()}
          </DocNumberLink>
        );
      },
    }),
    columnHelper.accessor("customerName", {
      id: "customerName",
      header: "Customer",
      size: 180,
      cell: (info) => {
        const row = info.row.original;
        return (
          <FilterPartyName
            name={info.getValue()}
            onClick={() => {
              const next = togglePartyFilter(actions.partyFilter, {
                id: row.customerId,
                name: row.customerName,
              });
              actions.setPartyFilter(next);
              flashIfSet(actions, row.id, next != null);
            }}
          />
        );
      },
    }),
    columnHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/sales/orders/${info.row.original.id}`}
        />
      ),
    }),
    columnHelper.display({
      id: "payPct",
      header: "Paid",
      size: 130,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <AxisProgress
            axis="Paid"
            format="money"
            current={r.amountPaid}
            max={r.total || r.grossTotal}
            onFilterClick={() => {
              const next = toggleFilter(
                actions.payFilter,
                r.pay as PayAxisFilter,
                "all",
              );
              actions.setPayFilter(next);
              flashIfSet(
                actions,
                r.id,
                shouldFlashAfterToggle(next, "all"),
              );
            }}
          />
        );
      },
    }),
    columnHelper.display({
      id: "deliverPct",
      header: "Delivered",
      size: 110,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <AxisProgress
            axis="Delivered"
            format="qty"
            current={r.qtyDelivered}
            max={r.qtyOrdered}
            onFilterClick={() => {
              const next = toggleFilter(
                actions.deliverFilter,
                r.deliver as DeliverAxisFilter,
                "all",
              );
              actions.setDeliverFilter(next);
              flashIfSet(
                actions,
                r.id,
                shouldFlashAfterToggle(next, "all"),
              );
            }}
          />
        );
      },
    }),
    columnHelper.accessor("grossTotal", {
      id: "grossTotal",
      header: "Total",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {info.getValue() ?? info.row.original.total}
        </span>
      ),
    }),
    columnHelper.display({
      id: "dueAt",
      header: "Due",
      size: 120,
      enableSorting: true,
      cell: ({ row }) => {
        const due = primaryDueBadge({
          paymentDueOn: row.original.paymentDueOn,
          deliverBy: row.original.deliverBy,
        });
        if (!due.label) return "—";
        const filter = dueBadgeToFilter(due);
        return (
          <DueBadge
            badge={due}
            onFilterClick={
              filter
                ? () => {
                    const next = toggleFilter(
                      actions.dueFilter,
                      filter,
                      "all",
                    );
                    actions.setDueFilter(next);
                    flashIfSet(
                      actions,
                      row.original.id,
                      shouldFlashAfterToggle(next, "all"),
                    );
                  }
                : undefined
            }
          />
        );
      },
    }),
    columnHelper.display({
      id: "assignees",
      header: "People",
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <AssigneeAvatars assignees={row.original.assignees} />
      ),
    }),
    columnHelper.display({
      id: "tags",
      header: "Tags",
      size: 140,
      enableSorting: false,
      cell: ({ row }) => (
        <ListTagPills
          tags={row.original.tags}
          activeTagId={actions.tagId || undefined}
          onFilterTag={(tagId) => {
            const next = toggleFilter(actions.tagId, tagId, "");
            actions.setTagId(next);
            flashIfSet(
              actions,
              row.original.id,
              shouldFlashAfterToggle(next, ""),
            );
          }}
        />
      ),
    }),
    columnHelper.accessor("docDate", {
      id: "docDate",
      header: "Date",
      size: 100,
      enableSorting: true,
      cell: (info) => {
        const raw = info.getValue();
        if (!raw) return "—";
        return <DocDateCell raw={raw} />;
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 48,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const docPath = `/c/${companySlug}/sales/orders/${row.original.id}`;
        return (
          <DeskDocListRowMenu
            docPath={docPath}
            assignPath={docPath}
            tabLinks={[
              {
                label: "Deliver",
                to: `${docPath}?tab=deliveries`,
              },
              {
                label: "Collect",
                to: `${docPath}?tab=payments`,
              },
            ]}
          />
        );
      },
    }),
  ]);
}
